import React, { useEffect, useState } from 'react';
import { Play, Search, Video, X } from 'lucide-react';
import {
  Alert, Badge, Button, Card, Empty, Input, Loading, Page, PageHead,
} from '../components/ui';
import { api } from '../lib/api';
import { titleise } from '../lib/format';

const QUICK = [
  { emoji: '💪', label: 'Chest workout' },
  { emoji: '🦵', label: 'Leg day' },
  { emoji: '🏃', label: 'Fat loss cardio' },
  { emoji: '🥗', label: 'High protein meals' },
  { emoji: '🧘', label: 'Mobility routine' },
  { emoji: '😴', label: 'Recovery and sleep' },
];

// The browser decodes HTML entities that the YouTube API returns in titles.
const decode = (s) => {
  const el = document.createElement('textarea');
  el.innerHTML = s || '';
  return el.value;
};

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [goal, setGoal] = useState(null);
  const [query, setQuery] = useState('');
  const [heading, setHeading] = useState('Recommended for you');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(null);

  const loadSuggestions = async () => {
    setLoading(true); setError(''); setHeading('Recommended for you');
    try {
      const res = await api.videos.suggestions();
      setVideos(res.videos || []);
      setGoal(res.goal);
    } catch (e) { setError(e.message); setVideos([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSuggestions(); }, []);

  const search = async (term) => {
    const q = (term ?? query).trim();
    if (!q) return;
    setQuery(q);
    setLoading(true); setError(''); setGoal(null);
    setHeading(`Results for "${q}"`);
    try {
      const res = await api.videos.search(q, 9);
      setVideos(res.videos || []);
    } catch (e) { setError(e.message); setVideos([]); }
    finally { setLoading(false); }
  };

  return (
    <Page>
      <PageHead
        title="Videos"
        actions={
          query || goal === null
            ? <Button icon={X} onClick={() => { setQuery(''); loadSuggestions(); }}>
                Back to suggestions
              </Button>
            : null
        }
      >
        Workout and nutrition videos, picked to match the goal in your profile.
      </PageHead>

      <Card>
        <div className="row" style={{ gap: 10 }}>
          <Input
            placeholder="Search for a workout, recipe or technique…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            style={{ flex: 1, minWidth: 220 }}
          />
          <Button variant="primary" icon={Search} onClick={() => search()} disabled={loading}>
            Search
          </Button>
        </div>
        <div className="row" style={{ marginTop: 12 }}>
          {QUICK.map((q) => (
            <Button key={q.label} size="sm" variant="quiet" onClick={() => search(q.label)} disabled={loading}>
              <span style={{ marginRight: 4 }}>{q.emoji}</span>{q.label}
            </Button>
          ))}
        </div>
      </Card>

      {error && (
        <Alert tone={error.toLowerCase().includes('quota') ? 'warn' : 'crit'}>
          {error.toLowerCase().includes('quota')
            ? 'The daily YouTube quota is used up. Video recommendations will come back tomorrow.'
            : error}
        </Alert>
      )}

      <Card
        title={heading}
        sub={goal ? `Matched to your ${titleise(goal).toLowerCase()} goal` : undefined}
        actions={goal ? <Badge tone="accent">{titleise(goal)}</Badge> : null}
        flush
      >
        {loading ? (
          <Loading label="Fetching videos…" />
        ) : videos.length === 0 ? (
          <Empty icon={Video} title="No videos to show">
            {!error
              ? 'Try a different search term.'
              : /quota/i.test(error)
                ? 'Try again once the daily quota resets.'
                : /not configured/i.test(error)
                  ? 'This needs a YouTube API key on the server. Every other feature is unaffected.'
                  : 'The video service did not respond. Every other feature is unaffected.'}
          </Empty>
        ) : (
          <div className="grid grid-3" style={{ padding: 16 }}>
            {videos.map((v) => (
              <article className="media" key={v.video_id}>
                <div
                  className="media-thumb"
                  role="button"
                  tabIndex={0}
                  onClick={() => setPlaying(v)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setPlaying(v)}
                  aria-label={`Play ${decode(v.title)}`}
                >
                  <img src={v.thumbnail} alt="" loading="lazy" />
                  <span className="media-play"><span><Play size={19} fill="currentColor" /></span></span>
                </div>
                <div className="media-meta">
                  <div className="media-title">{decode(v.title)}</div>
                  <div className="media-chan">{decode(v.channel)}</div>
                </div>
              </article>
            ))}
          </div>
        )}
      </Card>

      {playing && (
        <div
          onClick={() => setPlaying(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 60,
            background: 'rgba(4,6,12,.82)', backdropFilter: 'blur(4px)',
            display: 'grid', placeItems: 'center', padding: 24,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 940 }}>
            <div className="spread" style={{ marginBottom: 10 }}>
              <div className="grow" style={{ fontWeight: 600, fontSize: 14 }}>{decode(playing.title)}</div>
              <Button icon={X} onClick={() => setPlaying(null)} aria-label="Close video" />
            </div>
            <div style={{ aspectRatio: '16/9', borderRadius: 'var(--r)', overflow: 'hidden', background: '#000' }}>
              <iframe
                src={`${playing.embed_url}?autoplay=1`}
                title={decode(playing.title)}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', height: '100%', border: 0 }}
              />
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
